// «تازه‌های همیان» — tells a member what changed, the first time they open a
// build newer than the last one they were told about.
//
// There is no "first time after update" event to hang this on: the service
// worker reload in index.tsx is one way a new build arrives, a cold start on a
// closed tab is another. So the only question asked is "is this build newer
// than what this browser has seen?", answered from localStorage.
//
// Content lives in ./releases.ts, which every deploy has to fill in.

import { useEffect, useState } from 'react'
import { queueEvent } from 'utilities/analytics'
import { hasProfile, hasSession } from 'utilities/session'
import { APP_VERSION, RELEASES, Release } from './releases'
import { compareVersions, mayInterrupt, unseenReleases } from './rules'
import WhatsNewSheet, { CloseReason, WhatsNewSource } from './sheet'

export { APP_VERSION } from './releases'

const SEEN_KEY = 'whats-new:seen'
// Let the screen the member landed on paint before a sheet slides over it.
const SETTLE_MS = 900

const readSeen = (): string | null => {
    try {
        return window.localStorage.getItem(SEEN_KEY)
    } catch {
        return null
    }
}

/** Only ever moves forward: a rolled-back build must not re-announce later. */
const markSeen = (version: string) => {
    try {
        const seen = readSeen()
        if (!seen || compareVersions(version, seen) > 0)
            window.localStorage.setItem(SEEN_KEY, version)
    } catch {
        // Storage blocked: they may hear about this version again. Harmless.
    }
}

const readJoinedAt = (): number | null => {
    try {
        const created = JSON.parse(
            window.localStorage.getItem('profile') || 'null'
        )?.created
        return created ? Date.parse(created) : null
    } catch {
        return null
    }
}

const listeners = new Set<() => void>()

/** Open the full list of releases — the sidebar's version line does this. */
export const openWhatsNew = () => listeners.forEach((listener) => listener())

interface RouterLike {
    state: { location: { pathname: string } }
    subscribe: (listener: () => void) => () => void
}

interface Shown {
    isOpen: boolean
    releases: Release[]
    source: WhatsNewSource
    openedAt: number
}

export default function WhatsNew({ router }: { router: RouterLike }) {
    const [shown, setShown] = useState<Shown | null>(null)

    useEffect(() => {
        const show = (
            releases: Release[],
            source: WhatsNewSource,
            previous: string | null
        ) => {
            setShown({ isOpen: true, releases, source, openedAt: Date.now() })
            queueEvent('whats_new_shown', {
                version: APP_VERSION,
                previous_version: previous,
                source,
                releases_shown: releases.length,
                changes_shown: releases.reduce(
                    (sum, release) => sum + release.changes.length,
                    0
                ),
            })
        }

        const fromSidebar = () => show(RELEASES, 'sidebar', readSeen())
        listeners.add(fromSidebar)
        const stopListening = () => {
            listeners.delete(fromSidebar)
        }

        const lastSeen = readSeen()
        // A browser meeting Hamyan for the first time: nothing is "new" to it.
        if (lastSeen === null && !hasProfile()) {
            markSeen(APP_VERSION)
            return stopListening
        }
        const unseen = unseenReleases(RELEASES, lastSeen, readJoinedAt())
        if (unseen.length === 0) {
            markSeen(APP_VERSION)
            return stopListening
        }

        // Wait for a screen it is fine to cover, and a signed-in member on it.
        // Navigating during the pause restarts it on the new screen.
        let timer: ReturnType<typeof setTimeout> | undefined
        const ready = () =>
            hasSession() && mayInterrupt(router.state.location.pathname)
        const schedule = () => {
            clearTimeout(timer)
            if (!ready()) return
            timer = setTimeout(() => {
                if (!ready()) return
                unsubscribe()
                show(unseen, 'update', lastSeen)
            }, SETTLE_MS)
        }
        const unsubscribe = router.subscribe(schedule)
        schedule()

        return () => {
            stopListening()
            unsubscribe()
            clearTimeout(timer)
        }
    }, [router])

    const close = (via: CloseReason) => {
        if (!shown?.isOpen) return
        markSeen(APP_VERSION)
        queueEvent('whats_new_closed', {
            version: APP_VERSION,
            source: shown.source,
            via,
            seconds_open: Math.round((Date.now() - shown.openedAt) / 1000),
        })
        // Keep the releases while the sheet animates out.
        setShown({ ...shown, isOpen: false })
    }

    return (
        <WhatsNewSheet
            isOpen={!!shown?.isOpen}
            releases={shown?.releases ?? []}
            source={shown?.source ?? 'update'}
            onClose={close}
        />
    )
}
